import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CajaSet, ClimaMarcador, MarcadorCompleto, ScoreboardState } from "@/types";
import { PadelMarcador } from "@/components/marcador/PadelMarcador";

interface DeviceViewData {
  device: { id: number; name: string; type: "SCREEN_SCOREBOARD" };
  club: { id: number; nombre: string };
  court: { id: number; name: string };
  customerName: string | null;
  clima: ClimaMarcador;
  // Partido nativo en directo (el caso normal).
  marcador: MarcadorCompleto | null;
  // Sin partido nativo: instantánea de un marcador físico antiguo enganchado a la pista, si lo hay.
  scoreboard: ScoreboardState | null;
}

// Pista con un marcador físico antiguo (sin partido nativo): se adapta su instantánea al mismo diseño,
// con jugadores genéricos porque ese sistema no manda nombres ni estadísticas.
function desdeMarcadorAntiguo(s: ScoreboardState): MarcadorCompleto {
  const num = (k: string) => Number(s[k] ?? 0) || 0;
  const terminado = s.estado === "ganador";
  const sets = ([1, 2, 3] as const).map((n): CajaSet => {
    const a = num(`set_${n}_a`);
    const b = num(`set_${n}_b`);
    const cerrado = n < s.set_juego || (terminado && n === s.set_juego);
    if (cerrado) return { a, b, estado: a > b ? "ganado_a" : "ganado_b" };
    if (n === s.set_juego) return { a, b, estado: "en_curso" };
    return { a: 0, b: 0, estado: "pendiente" };
  }) as [CajaSet, CajaSet, CajaSet];
  return {
    estado: terminado ? "FINALIZADO" : "EN_JUEGO",
    generadoEn: new Date().toISOString(),
    transcurridoSegundos: 0,
    corriendo: false,
    descansoSegundos: null,
    ajustes: { formato: "SETS_3", desempate: "TIEBREAK", puntoDeOro: true, descansoImpares: false, vozTanteo: true, vozPuntos: true },
    marcador: {
      setActual: s.set_juego,
      puntosA: s.equipo_a,
      puntosB: s.equipo_b,
      tipoJuego: "normal",
      sets,
      saque: s.saque ?? 0,
      ganadorEquipo: s.winAllGameTeam ?? null,
    },
    jugadores: ([1, 2, 3, 4] as const).map((position) => ({
      position,
      nombre: `Jugador ${position}`,
      fotoUrl: null,
      winners: 0,
      erroresForzados: 0,
      erroresNoForzados: 0,
    })),
  };
}

// Marcador fijo de una pista (pantalla/TV, SCREEN_SCOREBOARD): el mismo diseño que la tablet de árbitro
// pero SIN controles — nadie la maneja, solo enseña el resultado. Entra por el token del dispositivo.
export function DeviceViewPage() {
  const { token } = useParams<{ token: string }>();

  const { data, isError, dataUpdatedAt } = useQuery({
    queryKey: ["device-view", token],
    queryFn: async () => (await api.get<DeviceViewData>(`/device-view/${token}`)).data,
    refetchInterval: 2000,
    // Un marcador tiene que seguir actualizándose aunque la pestaña no esté en primer plano.
    refetchIntervalInBackground: true,
    retry: false,
  });

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#353334] p-8 text-center text-xl text-white">
        Pantalla no disponible: el dispositivo no existe o está desactivado.
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-[#353334] text-white">Cargando…</div>;
  }

  const marcador = data.marcador ?? (data.scoreboard ? desdeMarcadorAntiguo(data.scoreboard) : null);

  return (
    <div className="fixed inset-0 bg-[#353334]">
      <PadelMarcador
        modo="tv"
        pista={data.court.name}
        club={data.club.nombre}
        clima={data.clima}
        marcador={marcador}
        recibidoEn={dataUpdatedAt}
      />
    </div>
  );
}
